import DecimalTextField from 'common/components/DecimalTextField'
import { fNumber } from 'common/utils/formatNumber'
import { GreenSwitch } from 'modules/inventory/components/IsActiveSwitch'
import { useIsPrimaryOwnerAndSecondaryOwnerAndAdmin } from 'modules/user/api/selector'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { InputAdornment, Stack, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/system'

type Props = {
  id: string
  title: string
  description?: string
  subModuleValues: { key: string; name: string; unit?: string; value?: string | number }[]
  enabled: boolean
  isLoading?: boolean
  updateThreshold: (id?: string, payload?: Record<string, any>) => void
  updateProfile: (id?: string, payload?: Record<string, any>) => void
}

export default function NotificationSettingCard({
  id,
  title,
  description,
  subModuleValues,
  enabled,
  isLoading = false,
  updateThreshold,
  updateProfile,
}: Props) {
  const theme = useTheme()
  const { t } = useTranslation(['notification', 'landing', 'pond'])
  const isOwnerAndSecondaryOwnerAndAdmin = useIsPrimaryOwnerAndSecondaryOwnerAndAdmin()

  // Internal cache for last saved values
  const savedValuesRef = React.useRef<Record<string, string>>(
    subModuleValues.reduce((acc, v) => {
      acc[v.key] = v.value !== undefined && v.value !== null ? String(v.value) : ''
      return acc
    }, {} as Record<string, string>)
  )

  // Local state for user input
  const [localValues, setLocalValues] = React.useState(savedValuesRef.current)
  const [errors, setErrors] = React.useState<Record<string, string>>({})

  const validateValue = (value: string) => {
    if (value === '') return 'Invalid number'
    return ''
  }

  React.useEffect(() => {
    // Compare localValues vs cached saved values
    const hasChanged = Object.keys(localValues).some(
      (key) => localValues[key] !== savedValuesRef.current[key]
    )
    if (!hasChanged) return // early exit

    const handler = setTimeout(() => {
      const hasEmpty = Object.values(localValues).some((v) => v === '')
      if (hasEmpty) return

      const hasError = Object.values(errors).some((e) => e)
      if (hasError) return

      const numericPayload = Object.fromEntries(
        Object.entries(localValues).map(([key, value]) => {
          const cleaned = String(value).replace(/,/g, '')
          const parsed = parseFloat(cleaned)
          return [key, isNaN(parsed) ? 0 : parsed]
        })
      )

      updateThreshold(id, { thresholds: numericPayload })

      // Update cache after successful save
      savedValuesRef.current = { ...localValues }
    }, 2000)

    return () => clearTimeout(handler)
  }, [localValues, errors, id, updateThreshold])

  return (
    <Paper
      elevation={0}
      sx={{
        fontSize: 13,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        mb: 0,
        width: '100%',
        bgcolor: 'background.paper',
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box width="100%">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            mb={1}
          >
            <Typography color={theme.palette.grey[600]} fontSize={18}>
              {title}
            </Typography>

            <GreenSwitch
              checked={enabled}
              onChange={(_, v) => updateProfile(id, { is_enabled: v })}
            />
          </Box>

          {description && (
            <Typography fontSize={20} fontWeight={600}>
              {subModuleValues.reduce((desc, v) => {
                const value = localValues[v.key]
                const isInteger = Number.isInteger(Number(value))
                const formattedValue = fNumber(value, { disabledDecimal: isInteger })
                return desc.replace(`$${v.key}`, formattedValue)
              }, description)}
            </Typography>
          )}

          {enabled && (
            <Box
              sx={{ width: '100%', height: 1.5, backgroundColor: theme.palette.grey[200], my: 2 }}
            />
          )}

          {enabled && (
            <Box
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              gap={2}
              mt={2}
            >
              {subModuleValues.map((v) => (
                <Stack key={v.key} spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                  <label
                    htmlFor={`${v.key}-${id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Typography component="span" color="error">
                      *
                    </Typography>
                    <Typography
                      fontSize={16}
                      fontWeight={600}
                      noWrap
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {v.name}
                    </Typography>
                  </label>

                  <DecimalTextField
                    field={{
                      name: v.key,
                      value: localValues[v.key],
                      onChange: (val: any) => {
                        const str = String(val)
                        const error = validateValue(str)
                        setErrors((prev) => ({ ...prev, [v.key]: error }))
                        setLocalValues((prev) => ({ ...prev, [v.key]: str }))
                      },
                      onBlur: () => {},
                      ref: () => {},
                    }}
                    error={Boolean(errors[v.key])}
                    endAdornment={<InputAdornment position="end">{v.unit}</InputAdornment>}
                    disabled={!isOwnerAndSecondaryOwnerAndAdmin}
                  />
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  )
}
